import { useId, type ReactNode } from 'react';
import { cx } from '../cx';

export type MapRegion = {
  id: string;
  name: string;
  /** SVG path in the map's viewBox. */ d: string;
  labelX: number;
  labelY: number;
};
export type RegionValue = {
  id: string;
  value: number;
  /** Formatted value for labels and the data table. */ display: string;
  /** Listings behind the value; with `minSample`, regions below it show no data. */
  sample?: number;
};

export type RegionMapProps = {
  viewBox: string;
  /** Every region's shape (e.g. FINLAND_REGIONS from utils/map/finland-region-geometry, with names in the page language). */
  regions: MapRegion[];
  /** Values for regions with enough listings; leave out regions under the minimum sample (they show "no data"). */
  data: RegionValue[];
  /** Accessible name of the figure ("Open jobs by region"). */
  title: string;
  /** Legend text for each step's range, formatted by the caller from the thresholds. */
  formatRange: (min: number, max: number) => ReactNode;
  noDataLabel: ReactNode;
  /** Sample and boundary attribution ("4 177 jobs. Boundaries: Statistics Finland, CC BY 4.0"). Required. */
  source: ReactNode;
  tableHeaders: { region: ReactNode; value: ReactNode };
  /** How many of the highest regions get a label on the map. */
  maxLabels?: number;
  /** Fewest listings a region needs to show a value; regions with fewer show no data. */
  minSample?: number;
  className?: string;
};

/** Four steps from the sun ramp; no data is elevated. */
const STEPS = ['fill-jc-forest-soft', 'fill-jc-cta', 'fill-jc-ink', 'fill-jc-ink'] as const;
const SWATCH = ['bg-jc-forest-soft', 'bg-jc-cta', 'bg-jc-ink', 'bg-jc-ink'] as const;

/** Quartile thresholds over the distinct values, so one dominant region does not flatten the rest. Pure. */
export function quartileThresholds(values: number[]): number[] {
  const distinct = [...new Set(values)].sort((a, b) => a - b);
  if (distinct.length === 0) return [];
  const q = (p: number) => distinct[Math.min(distinct.length - 1, Math.floor(p * distinct.length))];
  return [q(0.25), q(0.5), q(0.75)];
}

/** Which of the four steps a value falls in. Pure. */
export function stepIndex(value: number, thresholds: number[]): number {
  let i = 0;
  while (i < thresholds.length && value > thresholds[i]) i++;
  return i;
}

/**
 * A choropleth in four sun steps for counts or pay by region, always with a legend, a source and a
 * visually hidden data table (the machine-readable twin of the shapes). Server-rendered, no JS.
 */
export function RegionMap({
  viewBox,
  regions,
  data: input,
  minSample,
  title,
  formatRange,
  noDataLabel,
  source,
  tableHeaders,
  maxLabels = 5,
  className,
}: RegionMapProps) {
  const hatchId = `rm${useId().replace(/:/g, '')}`;
  const data = minSample ? input.filter((d) => (d.sample ?? 0) >= minSample) : input;
  const byId = new Map(data.map((d) => [d.id, d]));
  const thresholds = quartileThresholds(data.map((d) => d.value));
  const labelled = new Set(
    [...data]
      .sort((a, b) => b.value - a.value)
      .slice(0, maxLabels)
      .map((d) => d.id),
  );
  const legend = STEPS.map((_, i) => {
    const values = data.filter((d) => stepIndex(d.value, thresholds) === i).map((d) => d.value);
    return values.length ? { i, min: Math.min(...values), max: Math.max(...values) } : null;
  }).filter((x): x is { i: number; min: number; max: number } => x !== null);

  return (
    <figure className={cx('m-0 flex flex-col gap-3', className)}>
      <svg viewBox={viewBox} role="img" aria-label={title} className="mx-auto block h-auto w-full max-w-80">
        {/* No data is hatched, so it never reads as the lowest step in either mode. */}
        <defs>
          <pattern id={hatchId} width={5} height={5} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={5} height={5} className="fill-jc-elevated" />
            <line x1={0} y1={0} x2={0} y2={5} strokeWidth={1.5} className="stroke-jc-border-control" />
          </pattern>
        </defs>
        {regions.map((r) => {
          const d = byId.get(r.id);
          return (
            <path
              key={r.id}
              d={r.d}
              fillRule="evenodd"
              strokeWidth={0.75}
              fill={d ? undefined : `url(#${hatchId})`}
              data-no-data={d ? undefined : true}
              className={cx('stroke-jc-surface', d && STEPS[stepIndex(d.value, thresholds)])}
            >
              <title>{d ? `${r.name}: ${d.display}` : r.name}</title>
            </path>
          );
        })}
        {regions
          .filter((r) => labelled.has(r.id))
          .map((r) => (
            <text
              key={r.id}
              x={r.labelX}
              y={r.labelY}
              textAnchor="middle"
              aria-hidden="true"
              strokeWidth={3.5}
              strokeLinejoin="round"
              className="fill-jc-ink stroke-jc-surface font-display font-bold"
              style={{ paintOrder: 'stroke' }}
            >
              <tspan x={r.labelX} fontSize={11}>
                {r.name}
              </tspan>
              <tspan x={r.labelX} dy={12} fontSize={10}>
                {byId.get(r.id)?.display}
              </tspan>
            </text>
          ))}
      </svg>
      <figcaption className="flex flex-col items-center gap-1.5 text-xs text-jc-ink-secondary">
        <span className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
          {legend.map((l) => (
            <span key={l.i} className="inline-flex items-center gap-1.5 tabular-nums">
              <span
                aria-hidden="true"
                className={cx('size-3 rounded-jc-info ring-1 ring-inset ring-jc-border', SWATCH[l.i])}
              />
              {formatRange(l.min, l.max)}
            </span>
          ))}
          {data.length < regions.length ? (
            <span className="inline-flex items-center gap-1.5">
              <svg
                aria-hidden="true"
                viewBox="0 0 12 12"
                className="size-3 rounded-jc-info ring-1 ring-inset ring-jc-border"
              >
                <rect width={12} height={12} fill={`url(#${hatchId})`} />
              </svg>
              {noDataLabel}
            </span>
          ) : null}
        </span>
        <span>{source}</span>
      </figcaption>
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">{tableHeaders.region}</th>
            <th scope="col">{tableHeaders.value}</th>
          </tr>
        </thead>
        <tbody>
          {regions.map((r) => (
            <tr key={r.id}>
              <th scope="row">{r.name}</th>
              <td>{byId.get(r.id)?.display ?? noDataLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
